import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, AlertTriangle, Trash2, Package, Upload, Download, FileSpreadsheet, Pencil, Package2 } from "lucide-react";
import { toast } from "sonner";
import MaterialPickerDialog from "@/components/MaterialPickerDialog";

const empty = { name: "", unit: "Nos", quantity: 0, reorder_level: 10, warehouse: "Main", rate: 0, category: "General" };
const SAMPLE_CSV = `name,sku,unit,quantity,reorder_level,warehouse,rate,category
Portland Cement OPC 43,,Bag,300,120,Main,410,Cement
Fly Ash Bricks 230x110x75,,Nos,15000,5000,Site-A,7.5,Bricks
TMT Steel Bar 16mm,,Ton,12,6,Main,63500,Steel
White Cement (Birla),,Bag,45,20,Main,850,Cement
Ceramic Wall Tile 300x600,,Box,120,60,Site-A,720,Tiles
Granite Slab Absolute Black,,Sqft,180,100,Main,180,Stone
Marine Plywood 19mm,,Sheet,85,30,Main,3200,Wood
LED Panel 24W Round,,Nos,120,50,Site-A,650,Electrical
Copper Pipe 1/2 inch,,Metre,240,100,Main,280,Plumbing
Split AC 1.5 Ton (Voltas),,Nos,8,5,Main,42000,HVAC`;

function parseCSV(text) {
  const lines = text.replace(/\r/g,"").split("\n").filter(l => l.trim());
  if (!lines.length) return [];
  const parseLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"' && line[i+1] === '"') { cur += '"'; i++; }
      else if (c === '"') inQ = !inQ;
      else if (c === "," && !inQ) { out.push(cur); cur = ""; }
      else cur += c;
    }
    out.push(cur); return out;
  };
  const header = parseLine(lines[0]).map(h => h.trim().toLowerCase());
  return lines.slice(1).map(line => {
    const cells = parseLine(line);
    const obj = {};
    header.forEach((h, i) => { obj[h] = (cells[i] || "").trim(); });
    return obj;
  }).filter(r => r.name);
}

export default function Inventory() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [libraryImportOpen, setLibraryImportOpen] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/inventory").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ name:r.name||"", unit:r.unit||"Nos", quantity:Number(r.quantity||0), reorder_level:Number(r.reorder_level||10), warehouse:r.warehouse||"Main", rate:Number(r.rate||0), category:r.category||"General" }); setEditId(r.id); setOpen(true); };
  const add = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editId) { await api.patch(`/inventory/${editId}`, form); toast.success("Item updated"); }
      else { await api.post("/inventory", form); toast.success("Item added"); }
      load(); setOpen(false); setForm(empty); setEditId(null);
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete item?")) return; await api.delete(`/inventory/${id}`); load(); };
  const adjust = async (r, delta) => { await api.patch(`/inventory/${r.id}`, { quantity: Math.max(0, r.quantity + delta) }); load(); };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const text = await file.text();
      const items = parseCSV(text);
      if (!items.length) { toast.error("No valid rows found"); setImporting(false); return; }
      const { data } = await api.post("/inventory/bulk", { items });
      setImportResult(data);
      if (data.ok) toast.success(`Imported ${data.ok} items`);
      if (data.fail) toast.error(`${data.fail} rows failed`);
      load();
    } catch (err) {
      toast.error("Import failed: " + err.message);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "sample_inventory.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const importFromLibrary = async (mats) => {
    const codes = mats.map((m) => m.item_code);
    try {
      const { data } = await api.post("/inventory/import-from-library", { item_codes: codes });
      const msg = `${data.created} new items · ${data.skipped} already in stock`;
      if (data.created > 0) toast.success(msg);
      else toast.info(msg);
      setLibraryImportOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
  };

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div className="flex justify-between items-end">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Warehouse</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Inventory</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setLibraryImportOpen(true)} data-testid="import-from-library-btn"><Package2 className="w-4 h-4 mr-1.5" />From Material Library</Button>
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
            <DialogTrigger asChild><Button variant="outline" data-testid="import-inventory-btn"><Upload className="w-4 h-4 mr-1.5" />Import CSV</Button></DialogTrigger>            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Import Inventory from CSV</DialogTitle></DialogHeader>
              <div className="p-4 rounded-lg bg-muted/50 space-y-2">
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">CSV Format</div>
                <div className="text-xs font-mono">name,sku,unit,quantity,reorder_level,warehouse,rate,category</div>
                <ul className="text-xs text-muted-foreground space-y-1 pl-2">
                  <li>• Only <strong>name</strong> is required</li>
                  <li>• <strong>sku</strong>: leave blank to auto-generate</li>
                  <li>• <strong>unit</strong>: Bag, Ton, Nos, Sqm, Cum, Litre, etc.</li>
                  <li>• <strong>quantity</strong>, <strong>reorder_level</strong>, <strong>rate</strong>: numeric</li>
                </ul>
                <Button variant="ghost" size="sm" onClick={downloadSample} className="text-primary p-0 h-auto"><Download className="w-3.5 h-3.5 mr-1" /> Download sample CSV (10 rows)</Button>
              </div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleImport} className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-primary file:text-primary-foreground hover:file:bg-primary/90" data-testid="csv-file-input" />
              {importing && <div className="text-sm text-muted-foreground flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 animate-pulse" /> Importing…</div>}
              {importResult && (
                <div className={`p-3 rounded-lg ${importResult.ok > 0 ? "bg-accent/20 border border-accent/40" : "bg-destructive/10 border border-destructive/30"}`}>
                  <div className="text-sm font-medium">✓ {importResult.ok} imported · ⚠ {importResult.fail} failed</div>
                  {importResult.errors?.length > 0 && (
                    <ul className="text-xs text-destructive mt-2 space-y-0.5">{importResult.errors.map((er, i) => <li key={i}>• {er}</li>)}</ul>
                  )}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground"><span className="opacity-70">ℹ</span> To import from Excel: File → Save As → CSV (Comma delimited)</div>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} data-testid="new-inventory-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" />New Item</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editId ? "Edit Item" : "Add Item"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <Input className="col-span-2" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Input placeholder="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
                <Input placeholder="Warehouse" value={form.warehouse} onChange={(e) => setForm({ ...form, warehouse: e.target.value })} />
                <Input type="number" placeholder="Quantity" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} />
                <Input type="number" placeholder="Reorder Level" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: Number(e.target.value) })} />
                <Input className="col-span-2" type="number" placeholder="Rate ₹" value={form.rate} onChange={(e) => setForm({ ...form, rate: Number(e.target.value) })} />
              </div>
              <Button onClick={add} data-testid="save-inventory-btn" className="bg-primary text-primary-foreground">{editId ? "Update" : "Save"}</Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => {
          const low = r.quantity < r.reorder_level;
          return (
            <div key={r.id} className={`rounded-2xl p-5 bg-card border ${low ? "border-primary/40" : "border-border/60"}`}>
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-muted/60 flex items-center justify-center"><Package className="w-5 h-5 text-accent" strokeWidth={1.6} /></div>
                {low && <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-primary"><AlertTriangle className="w-3 h-3" />Low</div>}
              </div>
              <div className="mt-3 font-display text-lg">{r.name}</div>
              <div className="text-xs text-muted-foreground">{r.warehouse} • {r.sku}</div>
              <div className="mt-4 flex items-baseline gap-2"><span className="font-display text-3xl font-semibold">{r.quantity}</span><span className="text-muted-foreground text-sm">{r.unit}</span></div>
              <div className="mt-3 flex gap-2 items-center">
                <Button size="sm" variant="outline" onClick={() => adjust(r, -1)} data-testid={`inv-minus-${r.id}`}>−</Button>
                <Button size="sm" variant="outline" onClick={() => adjust(r, 1)} data-testid={`inv-plus-${r.id}`}>+</Button>
                <button onClick={() => openEdit(r)} data-testid={`edit-inventory-${r.id}`} className="p-1.5 text-primary hover:bg-muted/60 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(r.id)} data-testid={`delete-inventory-${r.id}`} className="ml-auto p-1.5 text-destructive hover:bg-muted/60 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>

      <MaterialPickerDialog
        open={libraryImportOpen}
        multi
        onClose={() => setLibraryImportOpen(false)}
        onPickMany={importFromLibrary}
      />
    </div>
  );
}
